import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';

interface CrossSpaLinkProps {
  to: string;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

// Define which routes belong to which SPA
const ADMIN_ROUTES = ['/admin', '/login', '/change-password', '/unauthorized'];
const PUBLIC_ROUTES = ['/', '/about', '/gallery'];

const isAdminRoute = (path: string): boolean => {
  return ADMIN_ROUTES.some(route => path.startsWith(route));
};

const isPublicRoute = (path: string): boolean => {
  return PUBLIC_ROUTES.some(route => path.startsWith(route));
};

const isCrossSpaNavigation = (currentPath: string, targetPath: string): boolean => {
  const currentIsAdmin = isAdminRoute(currentPath);
  const currentIsPublic = isPublicRoute(currentPath);
  const targetIsAdmin = isAdminRoute(targetPath);
  const targetIsPublic = isPublicRoute(targetPath);
  
  // If we can't determine the current or target SPA, assume it's cross-SPA
  if (!currentIsAdmin && !currentIsPublic) return true;
  if (!targetIsAdmin && !targetIsPublic) return true;
  
  // Cross-SPA if current and target are different SPAs
  return (currentIsAdmin && targetIsPublic) || (currentIsPublic && targetIsAdmin);
};

export const CrossSpaLink: React.FC<CrossSpaLinkProps> = ({ to, children, className, style }) => {
  const location = useLocation();
  
  // Check if this is cross-SPA navigation
  if (isCrossSpaNavigation(location.pathname, to)) {
    // Use regular anchor tag for cross-SPA navigation
    return (
      <a href={to} className={className} style={style}>
        {children}
      </a>
    );
  }
  
  // Use React Router Link for same-SPA navigation
  return (
    <Link to={to} className={className} style={style}>
      {children}
    </Link>
  );
};

// Styled version that can be used with styled-components
export const StyledCrossSpaLink = styled(CrossSpaLink)``;
